import ActivityBrowseLink from '../components/ActivityBrowseLink';
import { useEffect, useMemo, useState } from 'react';
import styles from './Familiales.module.css';
import { EventModel } from '../models/EventModel';
import { Helmet } from 'react-helmet-async';
import SubscriptionSection from '../components/SubscriptionSection';
import EventItem from '../components/EventItem';
import ExploreMoreLinks from '../components/ExploreMoreLinks';
import { buildFamilyActivitiesPageSeo, buildFamilyActivitiesSections } from '../utils/eventSeo';

type FamilySectionsState = {
    today: EventModel[];
    weekend: EventModel[];
    free: EventModel[];
    upcoming: EventModel[];
};

const emptyPreload = {};

export default function Familiales() {
    const preloaded = (window as any).__FAMILY_EVENTS__ ?? emptyPreload;
    const [familySections, setFamilySections] = useState<FamilySectionsState>(buildFamilyActivitiesSections(preloaded));

    useEffect(() => {
        setFamilySections(buildFamilyActivitiesSections(preloaded));
    }, [preloaded]);

    const pageSeo = useMemo(() => buildFamilyActivitiesPageSeo(), []);

    return <div style={{position: 'relative'}}>
        <Helmet>
            <title>{pageSeo.title}</title>
            <meta name="description" content={pageSeo.metaDescription} />
            <meta property="og:type" content="website" />
            <meta property="og:title" content={pageSeo.title} />
            <meta property="og:description" content={pageSeo.metaDescription} />
            <meta property="og:url" content="https://alentours.ca/ville-quebec/activites-familiales" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={pageSeo.title} />
            <meta name="twitter:description" content={pageSeo.metaDescription} />
            <link rel="canonical" href="https://alentours.ca/ville-quebec/activites-familiales" />
        </Helmet>
        <SubscriptionSection stickyBanner={true}/>

        <section className={styles.hero}>
            <h1>{pageSeo.h1}</h1>
            {pageSeo.introParagraphs.map((paragraph) => (
                <p key={paragraph} className={styles.editorialCopy}>{paragraph}</p>
            ))}
        </section>

        {pageSeo.sections.map((section) => {
            const events = familySections[section.key];
            return (
                <section key={section.id} id={section.id} className={styles.section}>
                    <h2>{section.title}</h2>
                    <p className={styles.sectionIntro}>{section.intro}</p>
                    {events.length > 0 && <div className={styles.eventsContainer}>
                        {events.map((event) =>
                            <EventItem event={event} key={`family_${section.key}_${event.id}`} reloadOnClick={true} />
                        )}
                    </div>}
                    {events.length === 0 && <p className={styles.emptyState}>{section.emptyText}</p>}
                    <ActivityBrowseLink page="family" section={section.key} title={section.title} />
                </section>
            );
        })}

        <section className={styles.bottomCopySection}>
            {pageSeo.bottomParagraphs.map((paragraph) => (
                <p key={paragraph} className={styles.editorialCopy}>{paragraph}</p>
            ))}
        </section>

        <ExploreMoreLinks links={[
            { to: '/ville-quebec/aujourdhui', label: "Voir quoi faire à Québec aujourd'hui", reloadDocument: true },
            { to: '/ville-quebec/activites-gratuites', label: 'Découvrir les activités gratuites à Québec', reloadDocument: true },
            { to: '/#search', label: 'Ouvrir la recherche complète des événements' }
        ]} />

        <SubscriptionSection />
    </div>;
}
